import {
  ClusterOutlined,
  FlagOutlined,
  IdcardOutlined,
  LoadingOutlined,
  NotificationOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { RESOURCES } from '@constants';
import { AlertsContextProvider } from '@contexts/alerts-context';
import { CurrentUserContextProvider } from '@contexts/current-user-context';
import { ImpersonationContextProvider } from '@contexts/impersonation-context';
import { SignalRContextProvider } from '@contexts/signalr-context';
import { useSessionUser } from '@hooks/use-session-user';
import { useSetPageTitle } from '@hooks/use-set-page-title';
import { accessControlProvider } from '@providers/access-control-provider';
import { useNotificationProvider } from '@refinedev/antd';
import { AuthProvider, I18nProvider, Refine } from '@refinedev/core';
import routerProvider from '@refinedev/react-router';
import { Spin } from 'antd';
import i18next from 'i18next';
import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import {
  checkSession,
  fetchCurrentUser,
  redirectToLogin,
  redirectToLogout,
} from '../auth/proxy-auth';
import { ConfigStylesProvider } from '../contexts/config-styles';
import { dataProvider } from '../providers/data-provider';

export const RefineContext = ({ children }: React.PropsWithChildren) => {
  const { user, updateUserData } = useSessionUser();

  const { loading } = useAsync(updateUserData, []);
  const { t } = useTranslation();
  useSetPageTitle();

  const memoizedAccessControl = useMemo(() => accessControlProvider(user), [user]);

  const authProvider: AuthProvider = useMemo(
    () => ({
      login: async () => {
        redirectToLogin('/');
        return { success: true };
      },
      logout: async () => {
        redirectToLogout();
        return { success: true };
      },
      onError: async error => {
        if (error.response?.status === 401) return { logout: true };
        return { error };
      },
      check: async () => {
        const session = await checkSession();
        if (session?.authenticated) return { authenticated: true };
        return { authenticated: false, redirectTo: '/login' };
      },
      getPermissions: async () => null,
      getIdentity: fetchCurrentUser,
    }),
    [],
  );

  const i18nProvider: I18nProvider = {
    translate: (key: string, options?: Record<string, unknown>) => t(key, options) as string,
    changeLocale: async (lang: string) => i18next.changeLanguage(lang),
    getLocale: () => i18next.language,
  };

  if (loading) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin indicator={<LoadingOutlined spin style={{ fontSize: 66 }} />} />
      </div>
    );
  }

  return (
    <ConfigStylesProvider>
      <Refine
        accessControlProvider={memoizedAccessControl}
        authProvider={authProvider}
        dataProvider={dataProvider(i18nProvider)}
        i18nProvider={i18nProvider}
        notificationProvider={useNotificationProvider}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          projectId: 'dEW1QZ-J4NIET-sHsPdq',
        }}
        resources={[
          { name: RESOURCES.dashboard, list: '/dashboard' },
          { name: RESOURCES.alerts, list: '/alerts' },
          {
            name: RESOURCES.devices,
            list: '/devices',
            meta: { canDelete: true, icon: <IdcardOutlined />, label: t('devices.devices') },
          },
          {
            name: RESOURCES.profiles,
            list: '/profiles',
            meta: { canDelete: true, icon: <ClusterOutlined />, label: t('profiles.profiles') },
          },
          {
            name: RESOURCES.customers,
            list: '/customers',
            meta: { canDelete: true, icon: <UsergroupAddOutlined />, label: t('customers.title') },
          },
          {
            name: RESOURCES.carriers,
            list: '/carriers',
            meta: { canDelete: true, icon: <FlagOutlined />, label: t('carriers.title') },
          },
          {
            name: RESOURCES.users,
            list: '/users',
            meta: { canDelete: true, icon: <UserOutlined />, label: t('users.title') },
          },
          {
            name: RESOURCES.logs,
            list: '/logs',
            meta: { canDelete: true, icon: <NotificationOutlined />, label: t('logs.title') },
          },
          {
            name: RESOURCES.applicationVersions,
            list: '/applicationVersions',
            meta: {
              canDelete: true,
              icon: <ClusterOutlined />,
              label: t('applicationVersions.title'),
            },
          },
          {
            name: RESOURCES.settings,
            list: '/settings',
            meta: { canDelete: true, icon: <SettingOutlined />, label: t('settings.settings') },
          },
        ]}
        routerProvider={routerProvider}
      >
        <CurrentUserContextProvider updateUserData={updateUserData} user={user}>
          <ImpersonationContextProvider onImpersonationChange={updateUserData}>
            <SignalRContextProvider>
              <AlertsContextProvider>{children}</AlertsContextProvider>
            </SignalRContextProvider>
          </ImpersonationContextProvider>
        </CurrentUserContextProvider>
      </Refine>
    </ConfigStylesProvider>
  );
};
